import { Send, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { usePaywallModal } from '@/stripe/PayWallModal'
import { usePaywall } from '@/stripe/PayWallProvider'

import { useChat } from '@/services/chat.service'

interface ComposeMessageProps {
    onChange?: (typing: boolean) => void
    prefillMessage?: string | null
}

export default function ComposeMessage({ onChange, prefillMessage }: ComposeMessageProps) {
    const [inputValue, setInputValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const textareaRef = useRef(null)
    const { sendMessage, stopCompletion, isStreaming } = useChat()
    const { hasAccess, refreshUsageStatus } = usePaywall()
    const prefillAppliedRef = useRef(false)

    const { showSubscriptionModal } = usePaywallModal()

    // Handle prefill message
    useEffect(() => {
        if (prefillMessage && !prefillAppliedRef.current) {
            setInputValue(prefillMessage)
            prefillAppliedRef.current = true
            // Focus the textarea after setting the value
            setTimeout(() => {
                textareaRef.current?.focus()
            }, 100)
        }
    }, [prefillMessage])

    async function handleSendMessage() {
        if (!hasAccess) {
            showSubscriptionModal()
            return
        }
        if (!inputValue.trim() || isStreaming) return
        const _message = inputValue
        setInputValue('')
        await sendMessage(_message)

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
        }

        // Re-check usage status — sending a message may cross the chat threshold
        const status = await refreshUsageStatus()
        if (status?.thresholdReached && !hasAccess) {
            showSubscriptionModal()
        }
    }

    useEffect(() => {
        const textarea = textareaRef.current
        if (textarea) {
            textarea.style.height = 'auto'
            textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
        }
    }, [inputValue])

    useEffect(() => {
        onChange?.(inputValue.trim().length > 0)
    }, [inputValue, onChange])

    return (
        <div className='space-y-2 '>
            {isStreaming && (
                <div className='flex items-center gap-2 px-3 py-2 text-sm text-gray-600 rounded-lg bg-gray-50'>
                    <div className='flex gap-1'>
                        <div className='w-1.5 h-1.5 bg-brand-500 rounded-full animate-bounce' />
                        <div
                            className='w-1.5 h-1.5 bg-secondary-500 rounded-full animate-bounce'
                            style={{ animationDelay: '0.1s' }}
                        />
                        <div
                            className='w-1.5 h-1.5 bg-brand-500 rounded-full animate-bounce'
                            style={{ animationDelay: '0.2s' }}
                        />
                    </div>
                    <span className='text-xs'>Generating response...</span>
                </div>
            )}

            <div
                className={`relative bg-white border rounded-xl transition-all  ${
                    isFocused
                        ? 'border-brand-100 ring-1 ring-brand-100'
                        : 'border-gray-200 shadow-sm hover:border-gray-300'
                }`}
            >
                <div className='flex items-end gap-2 p-2.5'>
                    <textarea
                        ref={textareaRef}
                        className='flex-1 min-h-[44px]  bg-transparent border-none outline-none focus:ring-0 resize-none text-gray-800 placeholder:text-gray-500 text-sm'
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                handleSendMessage()
                            }
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        placeholder='Type your message...'
                        disabled={isStreaming}
                        rows={1}
                    />

                    {isStreaming ? (
                        <button
                            onClick={stopCompletion}
                            className='flex-shrink-0 p-2 text-white transition-colors bg-red-500 rounded-lg hover:bg-red-600'
                            title='Stop generating'
                        >
                            <X className='w-4 h-4' />
                        </button>
                    ) : (
                        <button
                            onClick={handleSendMessage}
                            disabled={!inputValue.trim()}
                            className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                                inputValue.trim()
                                    ? 'bg-brand-500 hover:bg-brand-600 text-white'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                            title='Send message'
                        >
                            <Send className='w-4 h-4' />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
